import {TransactionEntryLine} from '@/types/transaction/TransactionEntryLine'
import {TransactionLineCreationInfo} from '@/types/transaction/TransactionLineCreationInfo'
import {format} from 'date-fns'
import {TransactionLine} from '@/types/transaction/TransactionLine'
import {cloneDeep} from 'lodash'

export const entryLinesToTransactionLines = (transactionEntryLines: TransactionEntryLine[]) => {

    const transactionLines: TransactionLineCreationInfo[] = []
    transactionEntryLines.forEach((entryLine) => {

        const {date, amount, invoiceUuid, description, accrualAccountNo, accrualDuration, accrualStartMonth, accrualStartYear, bban, kid, paymentDate, entryLineIndex} = entryLine
        const tl = {
            date: format(date, 'yyyy-MM-dd'),
            amount: amount !== 0 ? amount : 0,
            invoiceUuid,
            description,
            project: entryLine.project,
            department: entryLine.department,
            tags: entryLine.tags,
            accrualAccountNo,
            accrualDuration,
            accrualStartMonth,
            accrualStartYear,
            bankAccountNo: bban,
            invoiceNo: entryLine.invoiceNo,
            dueDate: entryLine.invoiceDueDate && format(entryLine.invoiceDueDate, 'yyyy-MM-dd'),
            kid,
            paymentDate: paymentDate && format(paymentDate, 'yyyy-MM-dd'),
            entryLineIndex
        }

        if (entryLine.debitAccount && entryLine.creditAccount) {
            transactionLines.push({
                ...tl,
                accountNo: entryLine.debitAccount,
                subAccountUuid: entryLine.debitSubAccount,
                vatOptionId: entryLine.vatOptionIdDebit ?? 1, // TODO: Fix this
                subAccountLines: entryLine.debitSubAccount ? entryLine.subAccountLines : []
            })
            transactionLines.push({
                ...tl,
                accountNo: entryLine.creditAccount,
                subAccountUuid: entryLine.creditSubAccount,
                amount: entryLine.amount !== 0 ? -entryLine.amount : 0,
                vatOptionId: entryLine.vatOptionIdCredit,
                subAccountLines: entryLine.creditSubAccount ? entryLine.subAccountLines : []
            })
        } else {
            transactionLines.push({
                ...tl,
                accountNo: entryLine.debitAccount || entryLine.creditAccount || '',
                subAccountUuid: entryLine.debitSubAccount || entryLine.creditSubAccount,
                amount: entryLine.debitAccount ? (entryLine.amount !== 0 ? entryLine.amount : 0) : (entryLine.amount > 0 ? -entryLine.amount : 0),
                vatOptionId: entryLine.debitAccount && entryLine.vatOptionIdDebit || entryLine.vatOptionIdCredit,
                subAccountLines: entryLine.debitSubAccount || entryLine.creditSubAccount ? entryLine.subAccountLines : []
            })
        }
    })
    return transactionLines
}

const addVatLineToSourceLine = (tls: TransactionLine[]) => {
    const vtls = tls.filter(tl => tl.sourceRef)

    vtls.forEach(vtl => {
        const sourceLine = tls.find(tl => tl.uuid === vtl.sourceRef)
        if (sourceLine && vtls.filter(tl => tl.sourceRef === vtl.sourceRef).length === 1) {
            sourceLine.amount += vtl.amount
        }
        tls.splice(tls.indexOf(vtl), 1)
    })
}

const findOpposite = (lines: TransactionLine[], line: TransactionLine): TransactionLine | undefined => {
    if (lines.some(line => line.entryLineIndex < 0)) {
        return lines.find(l =>
            new Date(l.date).getTime() === new Date(line.date).getTime() &&
            l.description === line.description &&
            l.amount === -line.amount &&
            l.accountNo !== line.accountNo)
    }

    return lines.find(l =>
        l.entryLineIndex === line.entryLineIndex
        && l.amount === -line.amount
        && l.description === line.description
        && new Date(l.date).getTime() === new Date(line.date).getTime()
    )
}

export const transactionLinesToEntries = (lines: TransactionLine[]) => {
    const tls = lines.filter(tl => !tl.correction && !tl.corrected)

    const transactionEntryLines: Array<TransactionEntryLine> = []
    addVatLineToSourceLine(tls)

    while (tls.length > 0) {
        const tl = tls.pop()
        if (!tl) throw new Error('Popped undefined transaction line')
        const entry: any = {
            date: new Date(tl.date),
            description: tl.description,
            amount: Math.abs(tl.amount),
            project: tl.project,
            department: tl.department,
            tags: tl.tags,
            entryLineIndex: tl.entryLineIndex
        }
        const opposite = findOpposite(tls.slice(), tl)
        const dueDate = tl.dueDate || opposite?.dueDate
        entry.invoiceUuid = tl.invoiceUuid || opposite?.invoiceUuid
        entry.invoiceNo = tl.invoiceNo || opposite?.invoiceNo
        entry.invoiceDueDate = dueDate ? new Date(dueDate) : null
        entry.kid = tl.kid || opposite?.kid
        entry.bban = tl.bankAccountNo || opposite?.bankAccountNo
        if (tl.amount > 0) {
            entry.debitAccount = tl.accountNo
            entry.debitSubAccount = tl.subAccountUuid
            entry.vatOptionIdDebit = tl.vatOptionId
            entry.creditAccount = opposite?.accountNo
            entry.creditSubAccount = opposite?.subAccountUuid
            entry.vatOptionIdCredit = opposite?.vatOptionId
        } else {
            entry.creditAccount = tl.accountNo
            entry.creditSubAccount = tl.subAccountUuid
            entry.vatOptionIdCredit = tl.vatOptionId
            entry.debitAccount = opposite?.accountNo
            entry.debitSubAccount = opposite?.subAccountUuid
            entry.vatOptionIdDebit = opposite?.vatOptionId
        }
        if (opposite) tls.splice(tls.indexOf(opposite), 1)

        transactionEntryLines.push(entry)
    }
    return transactionEntryLines.sort(sortByEntryLineIndex)
}

const sortByEntryLineIndex = (a: TransactionEntryLine, b: TransactionEntryLine): number => {
    if (a.entryLineIndex < 0) return 1
    if (b.entryLineIndex < 0) return -1
    return a.entryLineIndex - b.entryLineIndex
}

export const cloneFromTransactionLines = (Lines: TransactionLine[]) => {
    return transactionLinesToEntries(cloneDeep(Lines))
}